import React, { useState } from 'react';
import { Upload, Database, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import api from '../../services/api';

const SinapiUpload: React.FC = () => {
    const [file, setFile] = useState<File | null>(null);
    const [estado, setEstado] = useState('AC');
    const [mes, setMes] = useState(new Date().getMonth() + 1);
    const [ano, setAno] = useState(new Date().getFullYear());
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    const handleUpload = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!file) return;

        setLoading(true);
        setMessage(null);

        const formData = new FormData();
        formData.append('file', file);
        formData.append('estado', estado);
        formData.append('mes', mes.toString());
        formData.append('ano', ano.toString());

        try {
            const response = await api.post('/admin/sinapi/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                }
            });

            setMessage({ type: 'success', text: response.data.mensagem });
            setFile(null);
        } catch (error: any) {
            console.error('Erro no upload SINAPI:', error);
            const errorMsg = error.response?.data?.mensagem ||
                error.response?.data?.message ||
                'Erro ao realizar upload. Verifique a conexão e o tamanho do arquivo.';
            setMessage({
                type: 'error',
                text: errorMsg
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-1">
                <p className="text-[#374151] dark:text-[#E5E7EB] text-3xl font-bold leading-tight tracking-tight">Gestão SINAPI</p>
                <p className="text-[#6B7280] dark:text-[#9CA3AF] text-base font-normal leading-normal">
                    Faça o upload dos arquivos ZIP do SINAPI para atualizar a base de preços.
                </p>
            </div>

            <div className="bg-white dark:bg-[#111827] border border-[#E5E7EB] dark:border-[#374151] rounded-xl p-6 max-w-2xl">
                <form onSubmit={handleUpload} className="space-y-6">
                    <div className="grid grid-cols-3 gap-4">
                        <div className="flex flex-col gap-2">
                            <label className="text-sm font-medium text-[#374151] dark:text-[#E5E7EB]">Estado (UF)</label>
                            <select
                                value={estado}
                                onChange={(e) => setEstado(e.target.value)}
                                className="h-10 px-3 bg-[#F8F9FA] dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] rounded-lg text-sm focus:ring-2 focus:ring-[#2563EB] outline-none"
                            >
                                {['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'].map(uf => (
                                    <option key={uf} value={uf}>{uf}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex flex-col gap-2">
                            <label className="text-sm font-medium text-[#374151] dark:text-[#E5E7EB]">Mês</label>
                            <input
                                type="number"
                                min="1" max="12"
                                value={mes}
                                onChange={(e) => setMes(parseInt(e.target.value))}
                                className="h-10 px-3 bg-[#F8F9FA] dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] rounded-lg text-sm focus:ring-2 focus:ring-[#2563EB] outline-none"
                            />
                        </div>
                        <div className="flex flex-col gap-2">
                            <label className="text-sm font-medium text-[#374151] dark:text-[#E5E7EB]">Ano</label>
                            <input
                                type="number"
                                min="2000" max="2100"
                                value={ano}
                                onChange={(e) => setAno(parseInt(e.target.value))}
                                className="h-10 px-3 bg-[#F8F9FA] dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] rounded-lg text-sm focus:ring-2 focus:ring-[#2563EB] outline-none"
                            />
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-[#374151] dark:text-[#E5E7EB]">Arquivo ZIP do SINAPI</label>
                        <div className="relative border-2 border-dashed border-[#E5E7EB] dark:border-[#374151] rounded-xl p-8 flex flex-col items-center justify-center gap-3 hover:border-[#2563EB] transition-colors cursor-pointer group">
                            <input
                                type="file"
                                accept=".zip"
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                                className="absolute inset-0 opacity-0 cursor-pointer"
                            />
                            <div className="size-12 bg-[#2563EB]/10 rounded-full flex items-center justify-center text-[#2563EB] group-hover:scale-110 transition-transform">
                                <Upload className="size-6" />
                            </div>
                            <div className="text-center">
                                <p className="text-sm font-medium text-[#374151] dark:text-[#E5E7EB]">
                                    {file ? file.name : 'Clique para selecionar o arquivo ZIP'}
                                </p>
                                <p className="text-xs text-[#6B7280] dark:text-[#9CA3AF] mt-1">
                                    Apenas arquivos .zip (Máx. 50MB)
                                </p>
                            </div>
                        </div>
                    </div>

                    {message && (
                        <div className={`p-4 rounded-lg flex items-start gap-3 ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                            {message.type === 'success' ? <CheckCircle2 className="size-5 shrink-0" /> : <AlertCircle className="size-5 shrink-0" />}
                            <p className="text-sm font-medium">{message.text}</p>
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={!file || loading}
                        className="w-full flex items-center justify-center gap-2 h-12 px-6 bg-[#2563EB] text-white rounded-lg text-base font-semibold hover:bg-[#2563EB]/90 focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="size-5 animate-spin" />
                                <span>Processando arquivo (isso pode levar alguns minutos)...</span>
                            </>
                        ) : (
                            <>
                                <Database className="size-5" />
                                <span>Importar Dados SINAPI</span>
                            </>
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default SinapiUpload;
